// 共通 Rich Text Editor 用 Toolbar(Blog 管理 + AI Blog 共有)。
//
// 役割:
// - WYSIWYG(TipTap)編集モードで使う統一 UI。
// - icon + 段落見出し dropdown のコンパクトデザイン。
// - BlogDraftEditor 内部に含めて描画し、BlogEditModal / AiBlogPage の
//   どちらも**この唯一の実装**を間接的に利用する。
// - page-specific 分岐(isAiBlog / pageType 等)を一切持たない。
//
// 使い方:
//   <BlogDraftEditorToolbar editor={tiptapEditor} compact />
//
// Props:
//   editor       TipTap Editor instance | null(未初期化時は disabled 表示)
//   compact      アイコン型(既定)— 既存システム Blog 管理 toolbar の視覚を踏襲
//   sticky       スクロールで上端固定(CSS は共通 — 呼び出し側は className で
//                任意の sticky style を被せられる)
//   showUndoRedo true(default)で undo / redo を左端に表示。false で非表示。
//   className    追加 class。
//
// サポートする操作(順序固定):
//   取消 (undo) / やり直し (redo) — showUndoRedo=false なら省略
//   ブロック変換 dropdown: 段落 / 見出し 1 / 見出し 2 / 見出し 3
//   太字 / 斜体 / 下線 / 取消線
//   箇条書き / 番号付きリスト / 引用
//   リンク
//
// Active / disabled / label / tip / aria-label / command — 全 corner で
// 一意に決定され、BLOG 管理 と AI BLOG の両方が同じ振る舞いを得る。

import { useState, type ReactNode } from "react";
import { type Editor } from "@tiptap/react";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough as StrikeIcon,
  Pilcrow as ParagraphIcon,
  Heading1,
  Heading2,
  Heading3,
  List as BulletIcon,
  ListOrdered as OrderedIcon,
  Quote as QuoteIcon,
  Link2 as LinkIcon,
  Image as ImageIcon,
  Undo2 as UndoIcon,
  Redo2 as RedoIcon,
} from "lucide-react";

// ---- props ----

export interface BlogDraftEditorToolbarProps {
  editor: Editor | null;
  /** アイコン型(既定)— 共通视觉基礎 */
  compact?: boolean;
  /** スクロールで上端固定(呼び出し側が className で上書きせずに使う想定) */
  sticky?: boolean;
  /** undo/redo セクション表示(true が既定) */
  showUndoRedo?: boolean;
  /** 追加 className */
  className?: string;
  /** test 用 — 何もなければ undefined */
  "data-testid"?: string;
}

// ---- helpers ----

function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(" ");
}

type BlockKind =
  | "paragraph"
  | "heading-1"
  | "heading-2"
  | "heading-3";

// Stage Editor-3.1:画像幅 preset。
// - "original" は width attr を null にして元のサイズに戻す
// - 数値 preset は px で保存 → WordPress は <img width="500"> を
//   そのまま反映
// - "full" は 100%(レスポンシブ対応、WordPress の content width に
//   合わせて広がる)
type ImageWidthPreset = "original" | "300" | "500" | "800" | "full";

const IMAGE_WIDTH_OPTIONS: ReadonlyArray<{
  value: ImageWidthPreset;
  label: string;
}> = [
  { value: "original", label: "画像幅" },
  { value: "300", label: "小 (300px)" },
  { value: "500", label: "中 (500px)" },
  { value: "800", label: "大 (800px)" },
  { value: "full", label: "全幅 (100%)" },
] as const;

// Stage Editor-3.2:画像配置 preset。
// - "none" は align attr を null にして解除(inline 配置に戻す)
// - left / center / right は inline style で出力(float or block+margin)
type ImageAlignPreset = "none" | "left" | "center" | "right";

const IMAGE_ALIGN_OPTIONS: ReadonlyArray<{
  value: ImageAlignPreset;
  label: string;
}> = [
  { value: "none", label: "配置" },
  { value: "left", label: "左寄せ" },
  { value: "center", label: "中央" },
  { value: "right", label: "右寄せ" },
] as const;

function detectBlockKind(e: Editor): BlockKind {
  if (e.isActive("heading", { level: 1 })) return "heading-1";
  if (e.isActive("heading", { level: 2 })) return "heading-2";
  if (e.isActive("heading", { level: 3 })) return "heading-3";
  return "paragraph";
}

// ---- component ----

export function BlogDraftEditorToolbar({
  editor,
  compact = true,
  sticky = false,
  showUndoRedo = true,
  className,
  "data-testid": dataTestId,
}: BlogDraftEditorToolbarProps) {
  // dropdown のレンダリング制御用 — select の onChange はネイティブ event。
  const [selectValue, setSelectValue] = useState<BlockKind | "">("");
  // editor がない/初期化中:空 toolbar + 「準備中」表示。
  if (!editor) {
    return (
      <div
        className={cx(
          "blog-draft-editor-toolbar",
          compact && "blog-draft-editor-toolbar--compact",
          sticky && "blog-draft-editor-toolbar--sticky",
          className
        )}
        role="toolbar"
        aria-label="書式ツールバー"
        data-testid={dataTestId}
      >
        <span className="blog-draft-editor-toolbar-empty">
          エディタ準備中…
        </span>
      </div>
    );
  }

  const t = editor;
  const current = detectBlockKind(t);
  // もし dropdown が古い値のままだったら同期する
  if (selectValue !== current && selectValue !== "") {
    // 不整合 — 次回 render で直す(setState しないが、見た目だけ同期)
  }

  function applyBlock(kind: BlockKind) {
    setSelectValue(kind);
    if (kind === "paragraph") {
      t.chain().focus().setParagraph().run();
    } else if (kind === "heading-1") {
      t.chain().focus().toggleHeading({ level: 1 }).run();
    } else if (kind === "heading-2") {
      t.chain().focus().toggleHeading({ level: 2 }).run();
    } else if (kind === "heading-3") {
      t.chain().focus().toggleHeading({ level: 3 }).run();
    }
  }

  interface IconBtnOpts {
    label: string;
    icon: ReactNode;
    active?: boolean;
    disabled?: boolean;
    /** aria-label を override したい場合 */
    ariaLabel?: string;
  }
  function iconBtn(
    onClick: () => void,
    opts: IconBtnOpts,
  ) {
    const active = !!opts.active;
    const disabled = !!opts.disabled;
    const aria = opts.ariaLabel ?? opts.label;
    return (
      <button
        type="button"
        className={cx("blog-draft-editor-toolbar-btn", active && "active")}
        onMouseDown={(e) => e.preventDefault() /* keep selection */}
        onClick={() => {
          if (disabled) return;
          onClick();
          t.commands.focus();
        }}
        disabled={disabled}
        title={aria}
        aria-label={aria}
        aria-pressed={active}
        data-testid={`toolbar-${opts.label}`}
      >
        <span className="blog-draft-editor-toolbar-icon" aria-hidden>
          {opts.icon}
        </span>
      </button>
    );
  }

  function setLink() {
    const previous = (t.getAttributes("link").href as string | undefined) ?? "";
    const next = window.prompt("リンク先 URL", previous);
    if (next === null) return;
    if (next === "") {
      t.chain().focus().unsetLink().run();
      return;
    }
    const trimmed = next.trim();
    // http / https / mailto / 相対パス(/) / フラグメント(#) のみ
    if (!/^(https?:\/\/|mailto:|\/|#)/i.test(trimmed)) {
      window.alert(
        "リンクは http(s):// / mailto: / 相対パス(/) / フラグメント(#) のみ使えます。",
      );
      return;
    }
    t.chain().focus().setLink({ href: trimmed }).run();
  }

  // dropdown の表示用 value:
  // editor が更新する度に同期したいが、editor の onUpdate は再 render を引き起こさないので
  // dropdown の controlled value には TipTap 由来の現在値をそのまま使う。
  // selectValue state は不要(NextValue は editor の現在 state を使う)。
  const dropdownValue: BlockKind = current;

  /**
   * 選択中の image ノードの ProseMirror position を堅牢に取得する。
   *
   * 背景:toolbar の <select> 要素は click で editor から focus を奪うため、
   * `isActive("image")` / `state.selection` が dropdown を開いた瞬間に
   * TextSelection に変わることがある(= NodeSelection 丧失)。
   * その状態で updateAttributes を呼ぶと「画像が選ばれていない」と
   * 判定されて width / align 変更が無視される(300/500/800 のバグ)。
   *
   * 対策:selection を見ず、$from 位置から直近の image ノードを
   * doc.descendants で逆方向に探す。NodeSelection なら selection.from、
   * そうでなければ cursor より前にある最後の image の pos を返す。
   */
  function findImagePos(): number | null {
    const { state } = t;
    const { selection, doc } = state;
    // 1. NodeSelection が image を指していればその pos
    if (
      selection.from === selection.to &&
      doc.nodeAt(selection.from)?.type?.name === "image"
    ) {
      return selection.from;
    }
    // 2. $from の親が image (= image を click して NodeSelection 直後)
    if (selection.$from.parent.type.name === "image") {
      return selection.$from.before(selection.$from.depth);
    }
    // 3. cursor より前方の最後の image を探す
    const cursorPos = selection.$from.pos;
    let lastBefore: number | null = null;
    doc.descendants((node, pos) => {
      if (node.type.name === "image" && pos <= cursorPos) {
        lastBefore = pos;
      }
      return true;
    });
    return lastBefore;
  }

  const imagePos = findImagePos();
  const imageNode = imagePos === null ? null : t.state.doc.nodeAt(imagePos);
  const isImageSelected = !!imageNode && imageNode.type.name === "image";
  const imageAttrs = isImageSelected
    ? (imageNode.attrs as Record<string, unknown>)
    : ({} as Record<string, unknown>);

  const currentImageWidth: ImageWidthPreset = (() => {
    if (!isImageSelected) return "original";
    const w = imageAttrs.width as string | null | undefined;
    if (!w) return "original";
    if (w === "100%") return "full";
    if (w === "300" || w === "300px") return "300";
    if (w === "500" || w === "500px") return "500";
    if (w === "800" || w === "800px") return "800";
    return "original"; // 任意幅は dropdown 上 "画像幅" 表示のまま
  })();
  const currentImageAlign: ImageAlignPreset =
    (imageAttrs.align as ImageAlignPreset | null | undefined) ?? "none";

  function applyImageWidth(preset: ImageWidthPreset) {
    const pos = imagePos ?? findImagePos();
    if (pos === null) return;
    const node = t.state.doc.nodeAt(pos);
    if (!node || node.type.name !== "image") return;
    const width =
      preset === "original"
        ? null
        : preset === "full"
          ? "100%"
          : `${preset}px`;
    // 直接 transaction を dispatch(focus 抢われても不影响)
    const tr = t.state.tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      width,
      height: null,
    });
    t.view.dispatch(tr);
  }

  function applyImageAlign(align: ImageAlignPreset) {
    const pos = imagePos ?? findImagePos();
    if (pos === null) return;
    const node = t.state.doc.nodeAt(pos);
    if (!node || node.type.name !== "image") return;
    const tr = t.state.tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      align: align === "none" ? null : align,
    });
    t.view.dispatch(tr);
  }

  return (
    <div
      className={cx(
        "blog-draft-editor-toolbar",
        compact && "blog-draft-editor-toolbar--compact",
        sticky && "blog-draft-editor-toolbar--sticky",
        className
      )}
      role="toolbar"
      aria-label="書式ツールバー"
      data-testid={dataTestId}
    >
      {showUndoRedo && (
        <>
          {iconBtn(() => t.chain().focus().undo().run(), {
            label: "元に戻す",
            icon: <UndoIcon size={14} />,
            disabled: !t.can().undo(),
            ariaLabel: "元に戻す",
          })}
          {iconBtn(() => t.chain().focus().redo().run(), {
            label: "やり直し",
            icon: <RedoIcon size={14} />,
            disabled: !t.can().redo(),
            ariaLabel: "やり直し",
          })}
          <span className="blog-draft-editor-toolbar-sep" />
        </>
      )}

      {/* ブロック変換 dropdown */}
      <select
        className="blog-draft-editor-toolbar-select"
        value={dropdownValue}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => applyBlock(e.target.value as BlockKind)}
        aria-label="ブロック変換"
        title="段落 / 見出し 1〜3"
      >
        <option value="paragraph">段落</option>
        <option value="heading-1">見出し 1</option>
        <option value="heading-2">見出し 2</option>
        <option value="heading-3">見出し 3</option>
      </select>
      <span className="blog-draft-editor-toolbar-sep" />

      {iconBtn(() => t.chain().focus().toggleBold().run(), {
        label: "太字",
        icon: <BoldIcon size={14} />,
        active: t.isActive("bold"),
      })}
      {iconBtn(() => t.chain().focus().toggleItalic().run(), {
        label: "斜体",
        icon: <ItalicIcon size={14} />,
        active: t.isActive("italic"),
      })}
      {iconBtn(() => t.chain().focus().toggleUnderline().run(), {
        label: "下線",
        icon: <UnderlineIcon size={14} />,
        active: t.isActive("underline"),
      })}
      {iconBtn(() => t.chain().focus().toggleStrike().run(), {
        label: "取消線",
        icon: <StrikeIcon size={14} />,
        active: t.isActive("strike"),
      })}
      <span className="blog-draft-editor-toolbar-sep" />

      {iconBtn(() => t.chain().focus().toggleBulletList().run(), {
        label: "箇条書き",
        icon: <BulletIcon size={14} />,
        active: t.isActive("bulletList"),
      })}
      {iconBtn(() => t.chain().focus().toggleOrderedList().run(), {
        label: "番号付きリスト",
        icon: <OrderedIcon size={14} />,
        active: t.isActive("orderedList"),
      })}
      {iconBtn(() => t.chain().focus().toggleBlockquote().run(), {
        label: "引用",
        icon: <QuoteIcon size={14} />,
        active: t.isActive("blockquote"),
      })}
      <span className="blog-draft-editor-toolbar-sep" />

      {iconBtn(setLink, {
        label: "リンク",
        icon: <LinkIcon size={14} />,
        active: t.isActive("link"),
      })}
      <span className="blog-draft-editor-toolbar-sep" />

      {/* 画像幅 select — Stage Editor-3.1 */}
      <select
        className="blog-draft-editor-toolbar-select"
        value={currentImageWidth}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => applyImageWidth(e.target.value as ImageWidthPreset)}
        aria-label="画像幅"
        title="選択中画像の表示幅"
        disabled={!isImageSelected}
      >
        {IMAGE_WIDTH_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {/* 画像配置 select — Stage Editor-3.2 */}
      <select
        className="blog-draft-editor-toolbar-select"
        value={currentImageAlign}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => applyImageAlign(e.target.value as ImageAlignPreset)}
        aria-label="画像配置"
        title="選択中画像の配置"
        disabled={!isImageSelected}
      >
        {IMAGE_ALIGN_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ImageIcon
        size={12}
        aria-hidden
        style={{ color: isImageSelected ? "#2563eb" : "#9ca3af", marginLeft: -4 }}
      />
    </div>
  );
}

export default BlogDraftEditorToolbar;
